import type { Transaction } from "@rocicorp/zero";
import { dropUsagesLostByGroupRemoval } from "../connectorUsages";
import { type Schema, zql } from "../generatedDrizzleSchema";
import { grantsForScope } from "./grantQueries";
import {
  applyGrantDiff,
  diffGrants,
  insertGrantsDeduped,
} from "./validation/grantContent";
import type { GrantInput } from "./validation/grantShape";
import { assertGroupDeletable } from "./validation/groupDeletable";

type ReadTx = Pick<Transaction<Schema>, "run">;
type WriteTx = Pick<Transaction<Schema>, "run" | "mutate">;

type ExistingGrantRow = {
  id: string;
  userGroupId: string | null;
  userId: string | null;
  builtinRoleId: string | null;
  customRoleId: string | null;
};

// A user is a member of a group if they hold ANY grant scoped to it, whatever
// the role — the same definition the UI (buildUserGroupMemberships) and
// cross-resource availability use. The membership helpers below therefore read
// and remove across all roles; only the add path picks a role, conferring the
// standard `user_group_member`.
const userGrantsQuery = (groupId: string, tenantId: string) =>
  zql.roleGrants
    .where("scopeId", groupId)
    .where("tenantId", tenantId)
    .where("userId", "IS NOT", null);

export async function getGroupMemberIds(
  tx: ReadTx,
  groupId: string,
  tenantId: string
): Promise<string[]> {
  const grants = await tx.run(userGrantsQuery(groupId, tenantId));
  return [
    ...new Set(
      grants.map((g) => g.userId).filter((id): id is string => id != null)
    ),
  ];
}

// Deletes a group after checking it carries no external grants, clearing the
// membership/grants scoped to it first. Shared by the UI delete mutator and the
// SCIM delete endpoint so the two can't diverge on what cleanup a delete entails.
export async function deleteUserGroupCascade(
  tx: WriteTx,
  userGroupId: string,
  tenantId: string
): Promise<void> {
  await assertGroupDeletable(tx, userGroupId);
  const [group] = await tx.run(
    zql.userGroups.where("id", userGroupId).where("tenantId", tenantId)
  );
  if (!group) return;
  const memberIds = await getGroupMemberIds(tx, userGroupId, tenantId);
  await dropUsagesLostByGroupRemoval(
    tx,
    group.tenantId,
    userGroupId,
    memberIds
  );
  const scopedGrants = await tx.run(
    grantsForScope(userGroupId, group.tenantId)
  );
  for (const grant of scopedGrants) {
    await tx.mutate.roleGrants.delete({ id: grant.id });
  }
  await tx.mutate.userGroups.delete({ id: userGroupId });
}

// Role-aware reconciliation of the user grants on a group scope: diff the
// desired set against what exists, apply it, then drop the connector usages a
// removed user can no longer reach — the same disconnect semantics as the
// role-agnostic helpers below. Used by the UI set-grants mutator, which assigns
// arbitrary roles per user. Callers decide which existing grants to pass (e.g.
// membership only, or all user grants on the group).
export async function reconcileGroupUserGrants(
  tx: WriteTx,
  groupId: string,
  existing: readonly ExistingGrantRow[],
  desired: readonly GrantInput[],
  tenantId: string
): Promise<void> {
  const diff = diffGrants(existing, desired);
  await applyGrantDiff(tx, groupId, diff, tenantId);
  await dropUsagesLostByGroupRemoval(
    tx,
    tenantId,
    groupId,
    diff.removedUserIds
  );
}

// Reconciles a group's membership to exactly `desiredUserIds`. Membership is
// role-agnostic: a user dropped from the set loses every grant they hold on the
// group, and a newly added user gets the standard `user_group_member` role. Used
// by the SCIM PUT/replace paths, so it's authoritative — see removeGroupMembers.
export async function replaceGroupMembers(
  tx: WriteTx,
  groupId: string,
  desiredUserIds: Set<string>,
  tenantId: string
): Promise<void> {
  const currentIds = new Set(await getGroupMemberIds(tx, groupId, tenantId));
  await removeGroupMembers(
    tx,
    groupId,
    [...currentIds].filter((id) => !desiredUserIds.has(id)),
    tenantId
  );
  await addGroupMembers(
    tx,
    groupId,
    [...desiredUserIds].filter((id) => !currentIds.has(id)),
    tenantId
  );
}

// Idempotently adds `userIds` as members, conferring the standard
// `user_group_member` role. A user who already holds any grant on the group is
// already a member and is skipped, so a re-add is a no-op rather than a
// `role_grants_unique` violation or a duplicate role.
export async function addGroupMembers(
  tx: WriteTx,
  groupId: string,
  userIds: readonly string[],
  tenantId: string
): Promise<void> {
  const existing = await tx.run(userGrantsQuery(groupId, tenantId));
  const existingUserIds = new Set(existing.map((g) => g.userId));
  const toAdd = userIds.filter((id) => !existingUserIds.has(id));
  await insertGrantsDeduped(
    tx,
    toAdd.map((userId) => ({ userId, builtinRoleId: "user_group_member" })),
    groupId,
    tenantId
  );
}

// Removes `userIds` from the group. A removal proceeds even when it strips
// connector access the user still depends on: rather than leave the connector
// dangling and unusable, it drops the now-inaccessible connector usages (the
// personal chats, workflows, and git remotes that can no longer reach it).
// Membership is role-agnostic, so every grant a removed user holds on the
// group is deleted, not just the `user_group_member` role.
export async function removeGroupMembers(
  tx: WriteTx,
  groupId: string,
  userIds: readonly string[],
  tenantId: string
): Promise<void> {
  if (userIds.length === 0) return;
  const removeSet = new Set(userIds);
  const grants = await tx.run(userGrantsQuery(groupId, tenantId));
  for (const grant of grants) {
    if (grant.userId != null && removeSet.has(grant.userId)) {
      await tx.mutate.roleGrants.delete({ id: grant.id });
    }
  }
  await dropUsagesLostByGroupRemoval(tx, tenantId, groupId, userIds);
}
